import React from 'react';
import {
  Building2, CalendarClock, CalendarDays, ExternalLink, Mail, MapPin,
  Pencil, Phone, Trash2, User, Wallet, Briefcase,
} from 'lucide-react';
import { JobModalShell, primaryBtnStyle, secondaryBtnStyle } from './JobModalShell';
import { STAGES, T, companyAccent, companyInitials, fmtDate, safeExternalUrl, stageMeta } from '../constants';

const Row = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 0', borderBottom: `1px solid ${T.lineSoft}` }}>
      <Icon size={14} color={T.faint} strokeWidth={2} style={{ marginTop: 2, flexShrink: 0 }} />
      <div style={{ minWidth: 92, fontSize: 12, color: T.faint, fontWeight: 600, flexShrink: 0 }}>{label}</div>
      <div style={{ fontSize: 13, color: T.body, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
        {href
          ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: T.brand, fontWeight: 600, textDecoration: 'none' }}>{value}</a>
          : value}
      </div>
    </div>
  );
};

/** Read-only detail view with edit / move / delete actions. */
export const JobDetailsModal = ({ job, onEdit, onDelete, onMove, onClose }) => {
  const meta = stageMeta(job.stage);
  const accent = companyAccent(job.company);
  const jobUrl = safeExternalUrl(job.url);

  return (
    <JobModalShell
      title={job.title}
      subtitle={job.company}
      icon={Briefcase}
      accent={accent}
      onClose={onClose}
      maxWidth={560}
      footer={
        <>
          <button type="button" onClick={() => onDelete(job)}
            style={{ ...secondaryBtnStyle, color: T.brand, borderColor: T.brandBorder, marginRight: 'auto' }}
            onMouseEnter={e => { e.currentTarget.style.background = T.brandSoft; }}
            onMouseLeave={e => { e.currentTarget.style.background = T.surface; }}>
            <Trash2 size={14} /> Delete
          </button>
          {jobUrl && (
            <a href={jobUrl} target="_blank" rel="noopener noreferrer"
              style={{ ...secondaryBtnStyle, textDecoration: 'none' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.background = T.canvas; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.background = T.surface; }}>
              <ExternalLink size={14} /> View Job
            </a>
          )}
          <button type="button" onClick={() => onEdit(job)} style={primaryBtnStyle(false)}>
            <Pencil size={14} /> Edit
          </button>
        </>
      }
    >
      {/* Company header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 12, flexShrink: 0,
          background: `${accent}14`, border: `1px solid ${accent}28`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, fontWeight: 800, color: accent,
        }}>
          {companyInitials(job.company)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, color: T.ink }}>{job.company}</div>
          <div style={{ fontSize: 12.5, color: T.muted }}>{job.title}</div>
        </div>
      </div>

      {/* Move stage */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: T.body, marginBottom: 7 }}>Stage</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {STAGES.map((stage) => {
            const active = stage.id === job.stage;
            const Icon = stage.icon;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => !active && onMove(job._id, stage.id)}
                aria-pressed={active}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: active ? stage.bg : T.surface,
                  border: `1px solid ${active ? `${stage.color}55` : T.line}`,
                  color: active ? stage.color : T.muted,
                  borderRadius: 20, padding: '5px 11px',
                  fontSize: 11.5, fontWeight: active ? 700 : 600,
                  cursor: active ? 'default' : 'pointer', fontFamily: T.font,
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = `${stage.color}55`; e.currentTarget.style.color = stage.color; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = T.line; e.currentTarget.style.color = T.muted; } }}
              >
                <Icon size={12} strokeWidth={2.1} />
                {stage.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Details */}
      <div style={{ borderTop: `1px solid ${T.lineSoft}` }}>
        <Row icon={meta.icon}       label="Status"    value={meta.label} />
        <Row icon={MapPin}          label="Location"  value={job.location} />
        <Row icon={Wallet}          label="Salary"    value={job.salary} />
        <Row icon={Building2}       label="Job type"  value={job.jobType} />
        <Row icon={CalendarDays}    label="Applied"   value={fmtDate(job.appliedDate)} />
        <Row icon={CalendarClock}   label="Interview" value={fmtDate(job.interviewDate)} />
        <Row icon={User}            label="Contact"   value={job.contactName} />
        <Row icon={Mail}            label="Email"     value={job.contactEmail} href={job.contactEmail ? `mailto:${job.contactEmail}` : undefined} />
        <Row icon={Phone}           label="Phone"     value={job.contactPhone} href={job.contactPhone ? `tel:${job.contactPhone}` : undefined} />
        <Row icon={ExternalLink}    label="Job URL"   value={jobUrl} href={jobUrl || undefined} />
      </div>

      {job.notes && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: T.body, marginBottom: 6 }}>Notes</div>
          <div style={{
            background: T.canvas, border: `1px solid ${T.line}`, borderRadius: 10,
            padding: '11px 13px', fontSize: 13, color: T.body, lineHeight: 1.6, whiteSpace: 'pre-wrap',
          }}>
            {job.notes}
          </div>
        </div>
      )}
    </JobModalShell>
  );
};
